import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { timeAndLeaveGateway } from '../../time-and-leave';
import {
  AccrualMethod, CarryoverMode, ResetMode, TenureUnit, accrualMethodLabels,
  type LeavePlanSetupResponse, type LeavePlanSetupTypeResponse,
} from '../../time-and-leave/types';
import { ConfirmCard, ResultCard } from '../../../shared/components/ConfirmCard';
import {
  MAX_RULES, MONTH_NAMES, emptyForm, emptyRule, formFromSetup, methodUsesRules, requestFromForm, ruleFromLabel, ruleFromUnit, settingsFromForm,
  validateForm, type FormErrors, type LeavePlanForm, type RuleForm,
} from './leavePlanSetupForm';
import '../../compensation/compensation.css';
import '../employee-list.css';
import './employee-benefits-deductions.css';
import './employee-leave-plans.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M2.5 13.5h2.8l7.3-7.3-2.8-2.8-7.3 7.3zM11.2 2.8l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5M7 7v4M9 7v4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 5v6M5 8h6" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function RowAction({ label, tone, onClick, children }: { label: string; tone: 'edit' | 'danger'; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`row-action row-action--${tone}`} aria-label={label} title={label} onClick={onClick}>
      {children}
      <span className="row-action__label" aria-hidden="true">{label}</span>
    </button>
  );
}

const STATE_LAW_NOTE =
  'Check your state and local leave requirements before you set up a leave policy. This screen records the policy you choose; it does not decide what the law requires.';
const SETUP_ONLY_NOTE =
  'A custom reset date, the carryover choices and the negative balance limit are saved as setup only for now. The accrual engine resets on hire date or calendar year and applies only the carryover limit it already stores.';
const NOT_AUTOMATIC_NOTE =
  'Only Yearly accrual without service-duration rules is calculated automatically today. This setup is saved as setup only: no hours are added for it until accrual support for it is turned on, so keep tracking this balance by hand.';

const RATE_LABELS: Record<string, string> = {
  [AccrualMethod.EveryPayPeriod]: 'Hours Accrued / Pay Period',
  [AccrualMethod.HoursWorked]: 'Hours Earned',
  [AccrualMethod.Yearly]: 'Hours Accrued / Year',
};

/**
 * Admin Employee Profile Setup, Step 4: Additional Settings > Employee Leave Plans (Figma admin screens S01 to S16).
 *
 * What is stored, and where (docs/leave-plan-setup-contract.md):
 *  - the PLAN a row points at is a company leave plan owned by Time & Leave: Vacation Leave, Sick Leave, or a custom title that is created for this company
 *    only and then offered for other employees of the same company. It is never edited from an employee row.
 *  - the SETUP is this employee's enrollment: effective date, starting balance, accrual method and rules, reset, maximum balance, carryover and
 *    negative-balance choices. Edit changes only this employee's setup, never the company plan or another employee; type, title, effective date and
 *    starting balance are fixed once saved. "Delete" ends the setup and keeps every row and every balance entry, so earlier balances and paystubs are unchanged.
 *
 * Only Yearly accrual is calculated by the accrual engine today; the other methods are saved setup only and say so. Success is shown only after the server
 * confirmed the write.
 */
export function EmployeeLeavePlans({ employeeId }: { employeeId: string }) {
  const [setups, setSetups] = useState<LeavePlanSetupResponse[]>([]);
  const [customTypes, setCustomTypes] = useState<LeavePlanSetupTypeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<LeavePlanSetupResponse | 'new' | null>(null);
  const [form, setForm] = useState<LeavePlanForm>(() => emptyForm(todayIso()));
  const [baseline, setBaseline] = useState<LeavePlanForm>(() => emptyForm(todayIso()));
  const [errors, setErrors] = useState<FormErrors>({});
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'save' | 'discard' | null>(null);
  const [deleting, setDeleting] = useState<LeavePlanSetupResponse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ title: string; text: string } | null>(null);

  const addButtonRef = useRef<HTMLButtonElement>(null);
  const firstFieldRef = useRef<HTMLSelectElement>(null);
  const modalTitleId = useId();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [s, t] = await Promise.all([timeAndLeaveGateway.listLeavePlanSetups(employeeId), timeAndLeaveGateway.listLeavePlanSetupTypes()]);
      setSetups(s);
      setCustomTypes(t);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load leave plans.');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const isEdit = editing !== null && editing !== 'new';
  const today = todayIso();
  const checked = useMemo(() => validateForm(form, { isEdit, today }), [form, isEdit, today]);
  const valid = Object.keys(checked).length === 0;
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const shown = (key: string) => (showErrors ? errors[key] : undefined);

  function setField<K extends keyof LeavePlanForm>(key: K, value: LeavePlanForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setFormError(null);
  }

  function setRule(index: number, patch: Partial<RuleForm>) {
    setForm((f) => ({ ...f, rules: f.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));
    setFormError(null);
  }

  function pickType(key: string) {
    const existing = key.startsWith('plan:') ? customTypes.find((t) => `plan:${t.leavePlanId}` === key) : undefined;
    setForm((f) => ({ ...f, typeKey: key, title: existing ? existing.title : key === 'custom' ? f.title : '' }));
    setFormError(null);
  }

  function changeMethod(method: string) {
    setForm((f) => ({ ...f, accrualMethod: method, rules: methodUsesRules(method) ? f.rules : [] }));
    setFormError(null);
  }

  function openAdd() {
    const f = emptyForm(todayIso());
    setForm(f);
    setBaseline(f);
    setErrors({});
    setShowErrors(false);
    setFormError(null);
    setEditing('new');
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }

  function openEdit(s: LeavePlanSetupResponse) {
    const f = formFromSetup(s);
    setForm(f);
    setBaseline(f);
    setErrors({});
    setShowErrors(false);
    setFormError(null);
    setEditing(s);
  }

  function closeModal() {
    setEditing(null);
    setConfirm(null);
    setShowErrors(false);
    setFormError(null);
    setTimeout(() => addButtonRef.current?.focus(), 0);
  }

  async function write() {
    if (editing === null) return;
    setBusy(true);
    setFormError(null);
    try {
      if (editing === 'new') {
        await timeAndLeaveGateway.createLeavePlanSetup(employeeId, requestFromForm(form));
      } else {
        await timeAndLeaveGateway.updateLeavePlanSetup(editing.id, settingsFromForm(form));
      }
      await load();
      const wasEdit = editing !== 'new';
      setEditing(null);
      setConfirm(null);
      setNotice(
        wasEdit
          ? { title: 'Changes Saved!', text: 'Employee’s leave plan has been successfully updated.' }
          : { title: 'Leave Plan Added!', text: 'Employee’s leave plan has been successfully added.' },
      );
      addButtonRef.current?.focus();
    } catch (err) {
      setConfirm(null);
      // The entries are kept so nothing has to be retyped.
      setFormError(err instanceof Error ? err.message : 'Could not save. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!valid) {
      setErrors(checked);
      setShowErrors(true);
      return;
    }
    if (!isEdit) {
      void write();
      return;
    }
    if (!dirty) {
      closeModal();
      return;
    }
    setConfirm('save');
  }

  function discard() {
    if (!dirty) {
      closeModal();
      return;
    }
    setConfirm('discard');
  }

  async function confirmDelete() {
    if (!deleting) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await timeAndLeaveGateway.endLeavePlanSetup(deleting.id);
      await load();
      setDeleting(null);
      setNotice({ title: 'Item Successfully Deleted!', text: 'Employee’s leave plan has been removed going forward. Earlier balances and paystubs are unchanged.' });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not remove this leave plan. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  }

  const method = form.accrualMethod;
  const usesRules = methodUsesRules(method);
  const manual = method === String(AccrualMethod.Manual);
  const takenTypeKeys = new Set(setups.map((s) => (s.leavePlanType === 5 ? `plan:${s.leavePlanId}` : String(s.leavePlanType))));

  function fieldError(key: string) {
    const msg = shown(key);
    return msg ? <span className="error" role="alert">{msg}</span> : null;
  }

  function rateField(label: string, key: 'hoursPerPayPeriod' | 'hoursPerYear' | 'hoursEarned') {
    return (
      <label>
        {label} <span aria-hidden="true">*</span>
        <input
          inputMode="decimal" autoComplete="off" placeholder="0.00" value={form[key]} disabled={busy}
          aria-required="true" aria-invalid={shown(key) ? true : undefined}
          onChange={(e) => setField(key, e.target.value)}
        />
        {fieldError(key)}
      </label>
    );
  }

  return (
    <div className="lp employee-list">
      <div className="bd-toolbar">
        <button ref={addButtonRef} type="button" className="bd-add" onClick={openAdd}>+ Add Leave Plan</button>
      </div>

      {loadError && <p className="error" role="alert">{loadError}</p>}

      <div className="employee-list__table-wrap bd-table-wrap">
        <table className="employee-list__table bd-table">
          <thead>
            <tr>
              <th scope="col">Leave Plan Name</th>
              <th scope="col">Title</th>
              <th scope="col">Leave Hours</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="bd-empty">Loading…</td></tr>}
            {!loading && setups.length === 0 && (
              <tr>
                <td colSpan={4} className="bd-empty">
                  <svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
                    <path d="M4 10a2 2 0 0 1 2-2h9l3 4h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" fill="currentColor" />
                  </svg>
                  <div>No Data Available</div>
                </td>
              </tr>
            )}
            {setups.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.displayName}
                  {!s.accruesAutomatically && (
                    <span className="bd-badge" title={NOT_AUTOMATIC_NOTE}>Setup only</span>
                  )}
                </td>
                <td>{s.title ?? '-'}</td>
                <td>{s.leaveHours}</td>
                <td>
                  <span className="employee-list__actions">
                    <RowAction label="Edit" tone="edit" onClick={() => openEdit(s)}><EditIcon /></RowAction>
                    <RowAction label="Delete" tone="danger" onClick={() => { setDeleteError(null); setDeleting(s); }}><DeleteIcon /></RowAction>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="compensation-modal-backdrop" role="presentation">
          <div className="compensation-modal lp-modal" role="dialog" aria-modal="true" aria-labelledby={modalTitleId}>
            <div className="compensation-modal-head">
              <h3 id={modalTitleId}>Leave Plan Setup</h3>
              <button type="button" className="compensation-modal-close" aria-label="Close" onClick={discard} disabled={busy}>×</button>
            </div>
            <form onSubmit={submit} noValidate>
              <div className="compensation-modal-body">
                <label>
                  Leave Plan Type <span aria-hidden="true">*</span>
                  <select
                    ref={firstFieldRef} value={form.typeKey} disabled={isEdit || busy} aria-required="true"
                    aria-invalid={shown('typeKey') ? true : undefined} onChange={(e) => pickType(e.target.value)}
                  >
                    <option value="">Select a leave plan type</option>
                    <option value="1" disabled={takenTypeKeys.has('1') && form.typeKey !== '1'}>Vacation Leave</option>
                    <option value="2" disabled={takenTypeKeys.has('2') && form.typeKey !== '2'}>Sick Leave</option>
                    {customTypes.map((t) => (
                      <option key={t.leavePlanId} value={`plan:${t.leavePlanId}`} disabled={takenTypeKeys.has(`plan:${t.leavePlanId}`) && form.typeKey !== `plan:${t.leavePlanId}`}>
                        {t.title}
                      </option>
                    ))}
                    <option value="custom">Custom Leave</option>
                  </select>
                  {fieldError('typeKey')}
                </label>

                {(form.typeKey === 'custom' || form.typeKey.startsWith('plan:')) && (
                  <label>
                    Leave Plan Title <span aria-hidden="true">*</span>
                    <input
                      value={form.title} maxLength={100} autoComplete="off" disabled={isEdit || busy || form.typeKey.startsWith('plan:')}
                      aria-required="true" aria-invalid={shown('title') ? true : undefined} onChange={(e) => setField('title', e.target.value)}
                    />
                    {form.typeKey === 'custom' && <span className="lp-hint">A new title is saved for your company and offered for your other employees.</span>}
                    {fieldError('title')}
                  </label>
                )}

                <div className="bd-row">
                  <label>
                    Effective Date
                    <input
                      type="date" value={form.effectiveDate} disabled={isEdit || busy}
                      aria-invalid={shown('effectiveDate') ? true : undefined} onChange={(e) => setField('effectiveDate', e.target.value)}
                    />
                    {fieldError('effectiveDate')}
                  </label>
                  {!manual && (
                    <label>
                      Starting Balance (hours)
                      <input
                        inputMode="decimal" autoComplete="off" placeholder="00" value={form.startingBalance} disabled={isEdit || busy}
                        aria-invalid={shown('startingBalance') ? true : undefined} onChange={(e) => setField('startingBalance', e.target.value)}
                      />
                      {fieldError('startingBalance')}
                    </label>
                  )}
                </div>
                {isEdit && (
                  <p className="muted lp-hint">Type, title, effective date and starting balance are fixed once saved. Balances change only through accruals, leave taken and authorised adjustments.</p>
                )}

                <section className="lp-section" aria-labelledby={`${modalTitleId}-accrual`}>
                  <h4 id={`${modalTitleId}-accrual`}>Accrual Rules</h4>
                  <label>
                    Accrual Method
                    <select value={method} disabled={busy} aria-required="true" aria-invalid={shown('accrualMethod') ? true : undefined} onChange={(e) => changeMethod(e.target.value)}>
                      <option value="">Select an accrual method</option>
                      {Object.entries(accrualMethodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    {fieldError('accrualMethod')}
                  </label>

                  {method !== '' && (method !== String(AccrualMethod.Yearly) || form.rules.length > 0) && <p className="muted lp-note" role="note">{NOT_AUTOMATIC_NOTE}</p>}
                  {manual && (
                    <div className="lp-panel">
                      <label>
                        Starting Balance (Hours) <span aria-hidden="true">*</span>
                        <input
                          inputMode="decimal" autoComplete="off" placeholder="00" value={form.startingBalance} disabled={isEdit || busy}
                          aria-required="true" aria-invalid={shown('startingBalance') ? true : undefined} onChange={(e) => setField('startingBalance', e.target.value)}
                        />
                        <span className="lp-hint">Maximum balance settings below still apply. Admins update balances manually.</span>
                        {fieldError('startingBalance')}
                      </label>
                    </div>
                  )}

                  {method === String(AccrualMethod.EveryPayPeriod) && (
                    <div className="lp-panel">
                      {rateField('Hours per pay period', 'hoursPerPayPeriod')}
                      <span className="lp-hint">Accrues each cycle on payroll close</span>
                    </div>
                  )}
                  {method === String(AccrualMethod.Yearly) && (
                    <div className="lp-panel">
                      {rateField('Hours per year', 'hoursPerYear')}
                      <span className="lp-hint">Annual allocation granted on the reset date</span>
                    </div>
                  )}
                  {method === String(AccrualMethod.HoursWorked) && (
                    <div className="lp-panel">
                      <div className="bd-row">
                        {rateField('Hours earned', 'hoursEarned')}
                        <label>
                          Per hours worked <span aria-hidden="true">*</span>
                          <input
                            inputMode="decimal" autoComplete="off" placeholder="0.00" value={form.perHoursWorked} disabled={busy}
                            aria-required="true" aria-invalid={shown('perHoursWorked') ? true : undefined}
                            onChange={(e) => setField('perHoursWorked', e.target.value)}
                          />
                          {fieldError('perHoursWorked')}
                        </label>
                      </div>
                      <span className="lp-hint">Accrues each cycle on payroll close</span>
                    </div>
                  )}

                  {usesRules && (
                    <div className="lp-rules">
                      {form.rules.length === 0 ? (
                        <button type="button" className="lp-add" disabled={busy} onClick={() => setField('rules', [emptyRule(), emptyRule()])}><PlusIcon />Add New Rule</button>
                      ) : (
                        <>
                          <button type="button" className="lp-remove-all" disabled={busy} onClick={() => setField('rules', [])}><DeleteIcon />Remove Rule</button>
                          <div className="lp-rules-box">
                          <div className="lp-rules-wrap">
                            <table className="lp-rules-table">
                              <thead>
                                <tr>
                                  <th colSpan={2} scope="colgroup">Service Duration</th>
                                  <th scope="col">{RATE_LABELS[method]}</th>
                                  {method === String(AccrualMethod.HoursWorked) && <th scope="col">Per Hours Worked</th>}
                                  <th scope="col">Maximum Balance</th>
                                  <th scope="col">Remove</th>
                                </tr>
                                <tr className="lp-subhead">
                                  <th scope="col">From</th>
                                  <th scope="col">To</th>
                                  <th colSpan={method === String(AccrualMethod.HoursWorked) ? 4 : 3} />
                                </tr>
                              </thead>
                              <tbody>
                                {form.rules.map((r, i) => (
                                  <tr key={i}>
                                    <td className="lp-from">
                                      {ruleFromLabel(form.rules, i)} {ruleFromLabel(form.rules, i) !== '—' && (ruleFromUnit(form.rules, i) === String(TenureUnit.Years) ? 'Year(s)' : 'Month(s)')}
                                    </td>
                                    <td>
                                      <span className="lp-to">
                                        <input
                                          aria-label={`Rule ${i + 1} ends after`} inputMode="numeric" value={r.toValue} placeholder="∞" disabled={busy}
                                          aria-invalid={shown(`rule${i}.to`) ? true : undefined} onChange={(e) => setRule(i, { toValue: e.target.value })}
                                        />
                                        <select aria-label={`Rule ${i + 1} unit`} value={r.toUnit} disabled={busy} onChange={(e) => setRule(i, { toUnit: e.target.value })}>
                                          <option value={TenureUnit.Months}>Month(s)</option>
                                          <option value={TenureUnit.Years}>Year(s)</option>
                                        </select>
                                      </span>
                                      {fieldError(`rule${i}.to`)}
                                    </td>
                                    <td>
                                      <input aria-label={`Rule ${i + 1} ${RATE_LABELS[method]}`} inputMode="decimal" value={r.rate} placeholder="0" disabled={busy}
                                        aria-invalid={shown(`rule${i}.rate`) ? true : undefined} onChange={(e) => setRule(i, { rate: e.target.value })} />
                                      {fieldError(`rule${i}.rate`)}
                                    </td>
                                    {method === String(AccrualMethod.HoursWorked) && (
                                      <td>
                                        <input aria-label={`Rule ${i + 1} per hours worked`} inputMode="decimal" value={r.perHours} placeholder="0" disabled={busy}
                                          aria-invalid={shown(`rule${i}.perHours`) ? true : undefined} onChange={(e) => setRule(i, { perHours: e.target.value })} />
                                        {fieldError(`rule${i}.perHours`)}
                                      </td>
                                    )}
                                    <td>
                                      <input aria-label={`Rule ${i + 1} maximum balance`} inputMode="decimal" value={r.maxBalance} placeholder="0" disabled={busy}
                                        aria-invalid={shown(`rule${i}.max`) ? true : undefined} onChange={(e) => setRule(i, { maxBalance: e.target.value })} />
                                      {fieldError(`rule${i}.max`)}
                                    </td>
                                    <td>
                                      <button type="button" className="lp-row-remove" aria-label={`Remove rule ${i + 1}`} disabled={busy}
                                        onClick={() => setField('rules', form.rules.filter((_, j) => j !== i))}>×</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <button type="button" className="lp-add" disabled={busy || form.rules.length >= MAX_RULES} onClick={() => setField('rules', [...form.rules, emptyRule()])}>
                            <PlusIcon />Add New Rule
                          </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </section>

                <fieldset className="bd-fieldset lp-section">
                  <legend>Accrual Period</legend>
                  <p className="lp-q">When does the time off balance reset?</p>
                  <label className="bd-check"><input type="radio" name="lp-reset" checked={form.resetMode === String(ResetMode.EmployeeHireDate)} disabled={busy} onChange={() => setField('resetMode', String(ResetMode.EmployeeHireDate))} />Employee hire date</label>
                  <label className="bd-check"><input type="radio" name="lp-reset" checked={form.resetMode === String(ResetMode.CalendarYear)} disabled={busy} onChange={() => setField('resetMode', String(ResetMode.CalendarYear))} />Calendar year (January 1)</label>
                  <label className="bd-check"><input type="radio" name="lp-reset" checked={form.resetMode === String(ResetMode.CustomDate)} disabled={busy} onChange={() => setField('resetMode', String(ResetMode.CustomDate))} />Custom date</label>
                  {form.resetMode === String(ResetMode.CustomDate) && (
                    <div className="lp-panel">
                      <div className="bd-row">
                        <label>
                          Month <span aria-hidden="true">*</span>
                          <select value={form.resetMonth} disabled={busy} aria-required="true" onChange={(e) => setField('resetMonth', e.target.value)}>
                            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                          </select>
                          {fieldError('resetMonth')}
                        </label>
                        <label>
                          Day <span aria-hidden="true">*</span>
                          <input inputMode="numeric" value={form.resetDay} disabled={busy} aria-required="true" aria-invalid={shown('resetDay') ? true : undefined} onChange={(e) => setField('resetDay', e.target.value)} />
                          {fieldError('resetDay')}
                        </label>
                      </div>
                    </div>
                  )}
                  {fieldError('resetMode')}
                </fieldset>

                <fieldset className="bd-fieldset lp-section">
                  <legend>Maximum balance settings</legend>
                  <p className="lp-q">Should employee balances have a maximum limit?</p>
                  <label className="bd-check"><input type="radio" name="lp-max" checked={form.maxMode === 'none'} disabled={busy} onChange={() => setField('maxMode', 'none')} />No maximum balance</label>
                  <label className="bd-check"><input type="radio" name="lp-max" checked={form.maxMode === 'set'} disabled={busy} onChange={() => setField('maxMode', 'set')} />Yes, set a maximum balance</label>
                  {form.maxMode === 'set' && (
                    <div className="lp-panel">
                      <label>
                        Maximum allowed hours <span aria-hidden="true">*</span>
                        <input inputMode="decimal" value={form.maxBalance} disabled={busy} aria-required="true" aria-invalid={shown('maxBalance') ? true : undefined} onChange={(e) => setField('maxBalance', e.target.value)} />
                        {fieldError('maxBalance')}
                      </label>
                      <span className="lp-hint">Total hours an employee can ever hold in this plan</span>
                    </div>
                  )}
                </fieldset>

                <fieldset className="bd-fieldset lp-section">
                  <legend>Carryover settings</legend>
                  <p className="lp-q">What happens to unused time off at the end of the accrual period?</p>
                  <label className="bd-check"><input type="radio" name="lp-carry" checked={form.carryoverMode === String(CarryoverMode.Expires)} disabled={busy} onChange={() => setField('carryoverMode', String(CarryoverMode.Expires))} />Unused time expires</label>
                  <label className="bd-check"><input type="radio" name="lp-carry" checked={form.carryoverMode === String(CarryoverMode.CarryAll)} disabled={busy} onChange={() => setField('carryoverMode', String(CarryoverMode.CarryAll))} />Carry over all unused time</label>
                  <label className="bd-check"><input type="radio" name="lp-carry" checked={form.carryoverMode === String(CarryoverMode.CarryUpTo)} disabled={busy} onChange={() => setField('carryoverMode', String(CarryoverMode.CarryUpTo))} />Carry over up to maximum number of hours</label>
                  {form.carryoverMode === String(CarryoverMode.CarryUpTo) && (
                    <div className="lp-panel">
                      <label>
                        Maximum carryover hours <span aria-hidden="true">*</span>
                        <input inputMode="decimal" value={form.carryoverLimit} disabled={busy} aria-required="true" aria-invalid={shown('carryoverLimit') ? true : undefined} onChange={(e) => setField('carryoverLimit', e.target.value)} />
                        {fieldError('carryoverLimit')}
                      </label>
                      <span className="lp-hint">Hours above this amount will not roll into the next period</span>
                    </div>
                  )}
                  {fieldError('carryoverMode')}
                </fieldset>

                <fieldset className="bd-fieldset lp-section">
                  <legend>Negative balance settings</legend>
                  <p className="lp-q">Can employees request leave beyond their available balance?</p>
                  <label className="bd-check"><input type="radio" name="lp-neg" checked={form.negativeMode === 'no'} disabled={busy} onChange={() => setField('negativeMode', 'no')} />No, do not allow negative balance</label>
                  <label className="bd-check"><input type="radio" name="lp-neg" checked={form.negativeMode === 'yes'} disabled={busy} onChange={() => setField('negativeMode', 'yes')} />Yes, allow negative balance with admin approval</label>
                  {form.negativeMode === 'yes' && (
                    <div className="lp-panel">
                      <label>
                        Maximum negative balance
                        <input inputMode="decimal" value={form.maxNegative} disabled={busy} aria-invalid={shown('maxNegative') ? true : undefined} onChange={(e) => setField('maxNegative', e.target.value)} />
                        {fieldError('maxNegative')}
                      </label>
                      <span className="lp-hint">Leave blank for no limit. Enter the largest negative balance you&apos;ll allow.</span>
                    </div>
                  )}
                </fieldset>

                <p className="muted lp-note" role="note">{SETUP_ONLY_NOTE}</p>
                <p className="muted lp-note" role="note">{STATE_LAW_NOTE}</p>
                {formError && <p className="error" role="alert">{formError}</p>}
              </div>
              <div className="compensation-modal-footer">
                {isEdit ? (
                  <>
                    <button type="button" className="bd-btn bd-btn--danger" onClick={discard} disabled={busy}>Discard</button>
                    <button type="submit" className={`bd-btn ${valid ? 'bd-btn--save' : 'bd-btn--grey'}`} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                  </>
                ) : (
                  <button type="submit" className={`bd-btn ${valid ? 'bd-btn--save' : 'bd-btn--grey'}`} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {confirm === 'save' && (
        <ConfirmCard
          title="Save Changes?"
          body="You’ve made changes to this employee’s leave plan. Do you want to save your updates?"
          cancelLabel="Keep Editing" confirmLabel="Save Edit" confirmTone="save" busy={busy}
          onCancel={() => setConfirm(null)} onConfirm={() => void write()}
        />
      )}
      {confirm === 'discard' && (
        <ConfirmCard
          title="Discard Changes?"
          body="You’ve made changes to this employee’s leave plan. Do you want to discard your updates?"
          cancelLabel="Keep Editing" confirmLabel="Discard Edit" confirmTone="danger" busy={false}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            closeModal();
            setNotice({ title: 'Changes Discarded!', text: 'Your unsaved changes were discarded. The saved leave plan has not been changed.' });
          }}
        />
      )}
      {deleting && (
        <ConfirmCard
          title="Are you sure?"
          body="Are you sure you want to delete this leave plan from this employee? It stops applying going forward. Balance history, other employees and earlier paystubs are not changed."
          cancelLabel="Cancel" confirmLabel="Yes, Delete" confirmTone="danger" busy={busy} error={deleteError}
          onCancel={() => setDeleting(null)} onConfirm={() => void confirmDelete()}
        />
      )}

      {notice && <ResultCard title={notice.title} text={notice.text} onDismiss={() => setNotice(null)} />}
    </div>
  );
}
